import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { analytics } from '../lib/analytics';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', subscribe_newsletter: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      analytics.signupClick();
      const response = await axios.post(`${API}/auth/register`, formData);
      login(response.data.access_token, response.data.user);
      analytics.signupSuccess();
      
      // Show warning if newsletter subscription failed
      if (response.data.warning) {
        toast.warning(response.data.warning, { duration: 5000 });
      } else if (formData.subscribe_newsletter) {
        toast.success('Registration successful! You are now subscribed to our newsletter.');
      } else {
        toast.success('Registration successful!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="text-center mb-8">
          <Logo className="mx-auto mb-4 justify-center" />
          <h1 className="text-3xl font-bold mb-2">Create Account</h1>
          <p className="text-gray-600">Get started with Daily UAE Jobs</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
              data-testid="register-password-input"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>
          
          <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <input
              type="checkbox"
              id="newsletter"
              checked={formData.subscribe_newsletter}
              onChange={(e) => setFormData({ ...formData, subscribe_newsletter: e.target.checked })}
              className="mt-1"
              data-testid="newsletter-checkbox"
            />
            <div className="flex-1">
              <label htmlFor="newsletter" className="text-sm font-medium text-gray-900 cursor-pointer">
                Subscribe to Daily UAE Jobs newsletter
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Get UAE job tips and career advice in your inbox.
              </p>
            </div>
          </div>
          
          <Button type="submit" className="w-full btn-uae-red" disabled={loading} data-testid="register-submit-btn">
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#CE1126] font-medium hover:underline">
              Login here
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-[#CE1126]">
            ← Back to Home
          </Link>
        </div>
      </Card>
    </div>
  );
}
